import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Close,
  Smartphone,
} from '@mui/icons-material';

interface PassengerDocumentationDialogProps {
  open: boolean;
  onClose: () => void;
  pasajero: any;
  onSave?: (id: number, dniImg: string | null, pasaporteImg: string | null) => void;
}

const PassengerDocumentationDialog: React.FC<PassengerDocumentationDialogProps> = ({
  open,
  onClose,
  pasajero,
  onSave,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [dniImage, setDniImage] = useState<string | null>(pasajero?.dni_imagen || null);
  const [pasaporteImage, setPasaporteImage] = useState<string | null>(pasajero?.pasaporte_imagen || null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Sync state if passenger changes
  React.useEffect(() => {
    if (pasajero) {
      setDniImage(pasajero.dni_imagen || null);
      setPasaporteImage(pasajero.pasaporte_imagen || null);
    }
  }, [pasajero]);

  const PINK_THEME = '#e73c8d';
  const CANCEL_RED = '#d32f2f';
  const SAVE_GREEN = '#66bb6a';

  if (!pasajero) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (tabIndex === 0) setDniImage(reader.result as string);
        else setPasaporteImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const currentPreview = tabIndex === 0 ? dniImage : pasaporteImage;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 0,
          position: 'relative'
        }
      }}
    >
      <input
        type="file"
        hidden
        ref={fileInputRef}
        accept="image/*"
        onChange={handleFileChange}
      />
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #ddd'
      }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#444' }}>
          Agregar documentación
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Tabs 
          value={tabIndex} 
          onChange={(_, val) => setTabIndex(val)}
          variant="fullWidth"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: PINK_THEME,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#999',
              '&.Mui-selected': {
                color: PINK_THEME,
              },
            },
            mb: 4
          }}
        >
          <Tab label="Dni" />
          <Tab label="Pasaporte" />
        </Tabs>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 250,
          border: '1px solid #ddd',
          borderRadius: 1,
          bgcolor: '#fcfcfc',
          mb: 2,
          overflow: 'hidden',
          position: 'relative'
        }}>
          {currentPreview ? (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
               <img 
                src={currentPreview} 
                alt="Vista previa" 
                style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain' }} 
              />
              <Box sx={{ position: 'absolute', bottom: 10, display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                    textTransform: 'none',
                    fontSize: '0.7rem'
                  }}
                >
                  Cambiar imagen
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    if (tabIndex === 0) setDniImage(null);
                    else setPasaporteImage(null);
                  }}
                  sx={{
                    bgcolor: 'rgba(211, 47, 47, 0.8)',
                    color: 'white',
                    '&:hover': { bgcolor: '#b71c1c' },
                    textTransform: 'none',
                    fontSize: '0.7rem'
                  }}
                >
                  Eliminar imagen
                </Button>
              </Box>
            </Box>
          ) : (
            <Button
              variant="contained"
              startIcon={<Smartphone />}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                bgcolor: '#eee',
                color: '#333',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                border: '1px solid #ccc',
                '&:hover': {
                  bgcolor: '#e0e0e0',
                  boxShadow: 'none',
                },
                px: 3
              }}
            >
              Agregar imagen
            </Button>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 0, display: 'flex' }}>
        <Button 
          fullWidth
          onClick={onClose}
          sx={{ 
            bgcolor: CANCEL_RED, 
            color: 'white', 
            '&:hover': { bgcolor: '#b71c1c' },
            borderRadius: 0,
            height: 55,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '1.1rem'
          }}
        >
          Cancelar
        </Button>
        <Button 
          fullWidth
          onClick={() => {
            if (onSave && pasajero) {
              onSave(pasajero.id, dniImage, pasaporteImage);
            }
            onClose();
          }}
          sx={{ 
            bgcolor: SAVE_GREEN, 
            color: 'white', 
            '&:hover': { bgcolor: '#43a047' },
            borderRadius: 0,
            height: 55,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '1.1rem'
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PassengerDocumentationDialog;
